<template>
  <div class="ss-root" ref="root" :style="{ height: (STAGE_H * scale) + 'px' }">
    <div class="ss-stage" :style="{ transform: 'scale(' + scale + ')' }">
      <div class="ss-viewport">
        <!-- Everything below lives in one 3D world whose ground plane is the
             pitch. The "camera" is just the world's transform: panning and
             zooming it (with a CSS transition) follows the ball like a
             broadcast camera, and every stand/board/goal moves with it. -->
        <div class="ss-world" :style="worldStyle">
          <!-- Stands: planes rising from behind the ad-boards and leaning
               back, textured with a generated crowd. -->
          <div class="ss-stand ss-stand--far" :style="farStandStyle">
            <div class="ss-crowd ss-crowd--home" :style="{ backgroundImage: crowdHome }"></div>
            <div class="ss-crowd ss-crowd--away" :style="{ backgroundImage: crowdAway }"></div>
          </div>
          <div class="ss-stand ss-stand--left" :style="leftStandStyle">
            <div class="ss-crowd ss-crowd--full" :style="{ backgroundImage: crowdHome }"></div>
          </div>
          <div class="ss-stand ss-stand--right" :style="rightStandStyle">
            <div class="ss-crowd ss-crowd--full" :style="{ backgroundImage: crowdAway }"></div>
          </div>

          <!-- Grass around the pitch (up to the boards) and the pitch itself. -->
          <div class="ss-ground" :style="groundStyle"></div>
          <div class="ss-pitch">
            <div class="ss-pitch-shadow"></div>
            <div class="ss-momentum" :class="[momentumSide ? 'is-' + momentumSide : '', { 'is-visible': momentumVisible, 'is-possession': actionType === 11 }]"></div>
            <svg class="ss-lines" viewBox="0 0 105 68" preserveAspectRatio="none">
              <g fill="none" stroke="rgba(255,255,255,0.85)" stroke-width="0.35">
                <rect x="0" y="0" width="105" height="68" />
                <line x1="52.5" y1="0" x2="52.5" y2="68" />
                <circle cx="52.5" cy="34" r="9.15" />
                <rect x="0" y="13.84" width="16.5" height="40.32" />
                <rect x="0" y="24.84" width="5.5" height="18.32" />
                <path d="M 16.5,26.69 A 9.15,9.15 0 0 1 16.5,41.31" />
                <rect x="88.5" y="13.84" width="16.5" height="40.32" />
                <rect x="99.5" y="24.84" width="5.5" height="18.32" />
                <path d="M 88.5,26.69 A 9.15,9.15 0 0 0 88.5,41.31" />
                <path d="M 0,1 A 1,1 0 0 0 1,0" />
                <path d="M 104,0 A 1,1 0 0 0 105,1" />
                <path d="M 0,67 A 1,1 0 0 1 1,68" />
                <path d="M 105,67 A 1,1 0 0 0 104,68" />
              </g>
              <g fill="rgba(255,255,255,0.9)">
                <circle cx="52.5" cy="34" r="0.45" />
                <circle cx="11" cy="34" r="0.4" />
                <circle cx="94" cy="34" r="0.4" />
              </g>
            </svg>
          </div>

          <!-- Logo painted on the grass by the near touchline, at halfway. -->
          <div v-if="showSirplayLogo" class="ss-sideline-logos" :style="sidelineLogosStyle">
            <img :src="PITCH_LOGO" class="ss-sideline-logo" alt="" />
          </div>

          <!-- Set-piece cone (corner, throw-in, free kick, goal kick, penalty):
               a translucent fan on the grass from the ball toward its target. -->
          <div v-if="cone" :key="cone.key" class="ss-cone" :style="cone.style">
            <div class="ss-cone-fill" :style="cone.fillStyle"></div>
          </div>

          <!-- Ad-boards: one along the far touchline, one behind each goal. -->
          <!-- Each board scrolls left to right forever: two identical halves,
               shifted by exactly one half, so the loop has no seam. -->
          <div class="ss-board ss-board--far" :style="farBoardStyle">
            <div class="ss-board-track">
              <span v-for="h in 2" :key="'bf' + h" class="ss-board-half">
                <template v-for="n in farLogoCount">
                  <img v-if="logoUrl" :key="'i' + n" :src="logoUrl" class="ss-board-logo" :class="{ 'is-default': isDefaultBoardLogo, 'is-ready': logoReady }" :style="logoSlotStyle" alt="" />
                  <span v-else :key="'t' + n" class="ss-board-text">{{ boardText }}</span>
                </template>
              </span>
            </div>
          </div>
          <div v-for="b in endBoards" :key="'board-' + b.side" class="ss-board ss-board--end" :class="'ss-board--' + b.side" :style="b.style">
            <div class="ss-board-strip" :style="endStripStyle">
              <div class="ss-board-track">
                <span v-for="h in 2" :key="b.side + h" class="ss-board-half">
                  <template v-for="n in endLogoCount">
                    <img v-if="logoUrl" :key="'i' + n" :src="logoUrl" class="ss-board-logo" :class="{ 'is-default': isDefaultBoardLogo, 'is-ready': logoReady }" :style="logoSlotStyle" alt="" />
                    <span v-else :key="'t' + n" class="ss-board-text">{{ boardText }}</span>
                  </template>
                </span>
              </div>
            </div>
          </div>

          <!-- Goals: frame + net planes, the right one mirrored. -->
          <div v-for="g in goals" :key="'goal-' + g.side" class="ss-goal" :class="{ 'is-scored': g.scored }" :style="g.style">
            <div class="ss-net ss-net--back"></div>
            <div class="ss-net ss-net--top"></div>
            <div class="ss-net ss-net--side ss-net--side-a"></div>
            <div class="ss-net ss-net--side ss-net--side-b"></div>
            <div class="ss-goal-frame"></div>
          </div>

          <!-- Corner flags. -->
          <div v-for="f in flags" :key="f.key" class="ss-flag" :style="f.style"></div>

          <!-- Ball: a flat shadow on the grass plus a billboard sprite that
               always faces the camera. -->
          <div class="ss-ball-shadow" :style="ballShadowStyle"></div>
          <!-- Incident badge (shot, offside, ...) floating above the ball. -->
          <div v-if="badgeText && ballVisible" :key="'badge-' + badgeText" class="ss-badge-anchor" :style="badgeStyle">
            <span class="ss-badge">{{ badgeText }}</span>
          </div>
          <div class="ss-ball" :style="ballStyle"><span class="ss-ball-sprite"></span></div>
        </div>

        <!-- Stadium roof rim, flat on top of the scene. -->
        <svg class="ss-roof" viewBox="0 0 480 26" preserveAspectRatio="none">
          <defs>
            <linearGradient id="ssRoofRim" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stop-color="#e2345a" stop-opacity="0.9" />
              <stop offset="0.35" stop-color="#e2345a" stop-opacity="0.55" />
              <stop offset="0.5" stop-color="#e2345a" stop-opacity="0" />
            </linearGradient>
            <linearGradient id="ssRoofFill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stop-color="#050506" />
              <stop offset="1" stop-color="#141417" />
            </linearGradient>
          </defs>
          <path d="M0,0 H480 V6 Q240,24 0,6 Z" fill="url(#ssRoofFill)" />
          <path d="M0,6 Q240,24 480,6" fill="none" stroke="url(#ssRoofRim)" stroke-width="1.4" />
        </svg>

        <!-- Big incident badge (cards, shots, offside, VAR, goal): flat over
             the pitch so it stays legible whatever the camera is doing. -->
        <!-- During half time the "half time" card takes turns with the
             match summary (breakSlides): same card, sliding one after the other. -->
        <transition :name="summaryCycling ? 'ss-badge-swap' : 'ss-big-badge'" :mode="summaryCycling ? 'out-in' : undefined">
          <div v-if="summarySlide" :key="'summary-' + summarySlide.id" class="ss-big-badge is-summary"
               :class="['is-summary-' + summarySlide.type, 'is-' + summarySlide.kind]">
            <span class="ss-big-badge-icon">
              <span v-if="summarySlide.kind === 'yellow'" class="ss-icon-card"></span>
              <span v-else class="ss-summary-icon" v-html="SUMMARY_ICONS[summarySlide.kind]"></span>
            </span>
            <span class="ss-big-badge-title">{{ summarySlide.title }}</span>
            <!-- Team stat: both values, the higher one lit, and the split as a bar. -->
            <template v-if="summarySlide.type === 'team'">
              <span class="ss-summary-row">
                <span class="ss-summary-name is-home">{{ summarySlide.homeName }}</span>
                <span class="ss-summary-value" :class="{ 'is-lead': summarySlide.lead === '1' }">{{ formatSummary(summarySlide.home) }}{{ summarySlide.unit }}</span>
                <span class="ss-summary-sep">-</span>
                <span class="ss-summary-value" :class="{ 'is-lead': summarySlide.lead === '2' }">{{ formatSummary(summarySlide.away) }}{{ summarySlide.unit }}</span>
                <span class="ss-summary-name is-away">{{ summarySlide.awayName }}</span>
              </span>
              <span class="ss-summary-bar">
                <span class="ss-summary-bar-home" :style="{ flexGrow: summarySlide.home }"></span>
                <span class="ss-summary-bar-away" :style="{ flexGrow: summarySlide.away }"></span>
              </span>
            </template>
            <!-- Player stat: the best player, his team and the value. -->
            <span v-else class="ss-summary-row ss-summary-row--player">
              <span class="ss-summary-dot" :class="summarySlide.side === '2' ? 'is-away' : (summarySlide.side === '1' ? 'is-home' : 'is-neutral')"></span>
              <span class="ss-summary-player">
                <span class="ss-summary-player-name">{{ summarySlide.player }}</span>
                <span v-if="summarySlide.team" class="ss-summary-player-team">{{ summarySlide.team }}</span>
              </span>
              <span class="ss-summary-value is-lead">{{ formatSummary(summarySlide.value) }}</span>
            </span>
          </div>
          <div v-else-if="bigBadgeShown" :key="bigBadgeShown.key" class="ss-big-badge"
               :class="['is-' + bigBadgeShown.kind, bigBadgeShown.side === '2' ? 'is-away' : (bigBadgeShown.side === '1' ? 'is-home' : 'is-neutral')]">
            <span class="ss-big-badge-icon">
              <span v-if="bigBadgeShown.kind === 'yellow' || bigBadgeShown.kind === 'red'" class="ss-icon-card"></span>
              <svg v-else-if="bigBadgeShown.kind === 'goal'" viewBox="0 0 40 40">
                <circle cx="20" cy="20" r="17" fill="#fff" stroke="#111" stroke-width="1.5" />
                <polygon points="20,12 27.6,17.5 24.7,26.5 15.3,26.5 12.4,17.5" fill="#111" />
                <g stroke="#111" stroke-width="1.5" fill="none">
                  <line x1="20" y1="12" x2="20" y2="3.5" /><line x1="27.6" y1="17.5" x2="35.5" y2="15" />
                  <line x1="24.7" y1="26.5" x2="29.5" y2="33.5" /><line x1="15.3" y1="26.5" x2="10.5" y2="33.5" />
                  <line x1="12.4" y1="17.5" x2="4.5" y2="15" />
                </g>
              </svg>
              <svg v-else-if="bigBadgeShown.kind === 'on-target'" viewBox="0 0 40 40">
                <g fill="none" stroke="#22c55e" stroke-width="2.5">
                  <circle cx="20" cy="20" r="16" /><circle cx="20" cy="20" r="10" />
                </g>
                <circle cx="20" cy="20" r="4" fill="#22c55e" />
              </svg>
              <svg v-else-if="bigBadgeShown.kind === 'off-target'" viewBox="0 0 40 40">
                <path d="M6,36 V14 H28 V36" fill="none" stroke="#fff" stroke-width="2.5" stroke-linejoin="round" />
                <path d="M10,32 Q20,4 36,6" fill="none" stroke="#f87171" stroke-width="2.5" stroke-dasharray="3 2.5" stroke-linecap="round" />
                <path d="M31,2.5 L37,6 L32,10.5" fill="none" stroke="#f87171" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <svg v-else-if="bigBadgeShown.kind === 'offside'" viewBox="0 0 40 40">
                <line x1="10" y1="4" x2="10" y2="37" stroke="#e5e7eb" stroke-width="2.5" stroke-linecap="round" />
                <path d="M11,5 H34 V22 H11 Z" fill="#facc15" />
                <path d="M11,5 L34,22 H11 Z" fill="#ef4444" />
              </svg>
              <svg v-else-if="bigBadgeShown.kind === 'penalty' || bigBadgeShown.kind === 'missed-penalty'" viewBox="0 0 40 40">
                <path d="M4,20 V5 H36 V20" fill="none" stroke="#fff" stroke-width="2.5" stroke-linejoin="round" />
                <g stroke="rgba(255,255,255,0.35)" stroke-width="0.8">
                  <line x1="10" y1="5" x2="10" y2="20" /><line x1="16" y1="5" x2="16" y2="20" /><line x1="24" y1="5" x2="24" y2="20" /><line x1="30" y1="5" x2="30" y2="20" />
                  <line x1="4" y1="11" x2="36" y2="11" /><line x1="4" y1="16" x2="36" y2="16" />
                </g>
                <line x1="2" y1="20" x2="38" y2="20" stroke="#fff" stroke-width="1.5" />
                <circle cx="20" cy="31" r="6" fill="#fff" stroke="#111" stroke-width="1" />
                <polygon points="20,28 22.8,30 21.8,33.2 18.2,33.2 17.2,30" fill="#111" />
                <g v-if="bigBadgeShown.kind === 'missed-penalty'" stroke="#ef4444" stroke-width="3.2" stroke-linecap="round">
                  <line x1="27" y1="24" x2="37" y2="34" /><line x1="37" y1="24" x2="27" y2="34" />
                </g>
              </svg>
              <svg v-else-if="bigBadgeShown.kind === 'substitution'" viewBox="0 0 40 40">
                <!-- player in (green, up) / player out (red, down) -->
                <path d="M13,34 V10" stroke="#22c55e" stroke-width="3.5" stroke-linecap="round" fill="none" />
                <path d="M5,17 L13,7 L21,17" stroke="#22c55e" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" fill="none" />
                <path d="M27,6 V30" stroke="#ef4444" stroke-width="3.5" stroke-linecap="round" fill="none" />
                <path d="M19,23 L27,33 L35,23" stroke="#ef4444" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" fill="none" />
              </svg>
              <svg v-else-if="bigBadgeShown.kind === 'injury'" viewBox="0 0 40 40">
                <rect x="3" y="3" width="34" height="34" rx="8" fill="#fff" />
                <path d="M16,9 H24 V16 H31 V24 H24 V31 H16 V24 H9 V16 H16 Z" fill="#ef4444" />
              </svg>
              <svg v-else-if="bigBadgeShown.kind === 'whistle'" viewBox="0 0 40 40">
                <circle cx="31" cy="9" r="4" fill="none" stroke="#e5e7eb" stroke-width="2" />
                <path d="M13,13 H33 a3,3 0 0 1 3,3 V19 a3,3 0 0 1 -3,3 H24" fill="#e5e7eb" />
                <circle cx="14" cy="24" r="11" fill="#e5e7eb" />
                <circle cx="14" cy="24" r="4" fill="#1f2937" />
              </svg>
              <svg v-else-if="bigBadgeShown.kind === 'var'" viewBox="0 0 40 40">
                <rect x="3" y="7" width="34" height="22" rx="3" fill="none" stroke="#38bdf8" stroke-width="2.5" />
                <line x1="14" y1="35" x2="26" y2="35" stroke="#38bdf8" stroke-width="2.5" stroke-linecap="round" />
                <line x1="20" y1="29" x2="20" y2="35" stroke="#38bdf8" stroke-width="2.5" />
                <text x="20" y="22.5" text-anchor="middle" font-family="Roboto, Arial, sans-serif" font-weight="900" font-size="11" fill="#fff">VAR</text>
              </svg>
            </span>
            <span class="ss-big-badge-text">
              <span class="ss-big-badge-title">{{ bigBadgeShown.title }}</span>
              <span v-if="bigBadgeShown.team" class="ss-big-badge-team">{{ bigBadgeShown.team }}</span>
            </span>
          </div>
        </transition>

        <!-- Goal, two acts on one full-screen panel sliding in from the left:
             1) "GOL!" with the scoring team;
             2) the screen splits in the two teams' colours, names and scores
                roll up from the bottom, then the scorer's number rolls to the
                new score; the panel then leaves to the right. -->
        <transition name="ss-goal-panel">
          <div v-if="goalShown" :key="goalShown.key" class="ss-goal-panel" :class="goalShown.scoreSide === '2' ? 'is-away' : 'is-home'">
            <transition name="ss-goal-act" mode="out-in">
              <div v-if="goalAct === 'word'" key="word" class="ss-goal-act ss-goal-act--word">
                <div class="ss-goal-word">{{ goalShown.title }}</div>
                <div class="ss-goal-team-line">
                  <template v-if="goalShown.subtitle">{{ goalShown.subtitle }} · </template>{{ goalShown.team }}
                </div>
              </div>
              <div v-else key="split" class="ss-goal-act ss-goal-act--split">
                <div class="ss-goal-half ss-goal-half--home">
                  <span class="ss-goal-roll-in ss-goal-half-name"><span>{{ goalShown.homeName }}</span></span>
                  <span class="ss-goal-big-digit">
                    <transition name="ss-roll" mode="out-in"><span :key="'h' + rollHome" class="ss-goal-big-digit-value" :class="{ 'is-intro': !goalRolled }">{{ rollHome }}</span></transition>
                  </span>
                </div>
                <div class="ss-goal-half ss-goal-half--away">
                  <span class="ss-goal-roll-in ss-goal-half-name"><span>{{ goalShown.awayName }}</span></span>
                  <span class="ss-goal-big-digit">
                    <transition name="ss-roll" mode="out-in"><span :key="'a' + rollAway" class="ss-goal-big-digit-value" :class="{ 'is-intro': !goalRolled }">{{ rollAway }}</span></transition>
                  </span>
                </div>
                <div class="ss-goal-caption">{{ goalShown.label }} {{ goalShown.team }}</div>
              </div>
            </transition>
          </div>
        </transition>

        <div class="ss-clock">
          <span class="ss-clock-time">{{ time }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PITCH_LOGO, BOARD_LOGO, sirplayLicenceMixin } from '@/services/matchtracker/broadcastScene';
// World units: the pitch is PW x PH (105m x 68m), everything else is laid out
// around it in the same units. The stage is a fixed-size broadcast frame
// (STAGE_W x STAGE_H) scaled to the container width.
const STAGE_W = 480;
const STAGE_H = 240;
const PW = 420;
const PH = 272;
const FAR_GAP = 20;      // pitch -> far ad-board
const END_GAP = 34;      // goal line -> end ad-board
const BOARD_H = 19;
// Logo on the grass by the near touchline (world px, 4 px = 1 m): how
// tall and how far outside the line.
const SIDELINE_H = 19;
const SIDELINE_GAP = 3;
const TILT = 58;

// Crowd texture in a team's colour: dark seats tinted with the shirt colour
// and supporters wearing shades of it (plus a few neutral shirts), so each
// half of the stadium reads as that team's end. Generated once per colour
// and shared by every instance.
// Board logos: width/height ratio of each logo URL once loaded, shared by
// every instance (a remount shows the logo straight away).
const logoAspectCache = {};
const BOARD_LOGO_GAP = 26;
const BOARD_LOGO_FALLBACK_WIDTH = 90;
const crowdCache = {};
const hexRgb = hex => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
// t > 0 mixes toward white, t < 0 toward black.
const mixHex = (hex, t) => {
  const target = t > 0 ? 255 : 0;
  const k = Math.abs(t);
  return '#' + hexRgb(hex).map(c => Math.round(c + (target - c) * k).toString(16).padStart(2, '0')).join('');
};
function crowdTexture(color) {
  const base = /^#[0-9a-f]{6}$/i.test(color || '') ? color.toLowerCase() : '#6b6b72';
  if (crowdCache[base] !== undefined) return crowdCache[base];
  let url = '';
  try {
    const w = 180, h = 90;
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    // Seats: the team colour, very dark.
    ctx.fillStyle = mixHex(base, -0.82);
    ctx.fillRect(0, 0, w, h);
    const shirts = [base, base, base, mixHex(base, -0.25), mixHex(base, -0.45), mixHex(base, 0.3), mixHex(base, 0.55), '#e8e8e8', '#2e2e33'];
    const skins = ['#e0b899', '#c69474', '#8d5b3e', '#f1d0b5', '#6e4630'];
    for (let y = 0; y < h; y += 4) {
      const off = (y / 4) % 2 ? 1.5 : 0;
      for (let x = 0; x < w; x += 3) {
        if (Math.random() < 0.12) {
          // empty seat: a slightly lighter seat back
          ctx.fillStyle = mixHex(base, -0.65);
          ctx.fillRect(x + off, y + 1.5, 2, 2);
          continue;
        }
        ctx.fillStyle = shirts[Math.floor(Math.random() * shirts.length)];
        ctx.fillRect(x + off, y + 1.5, 2, 2.5);
        ctx.fillStyle = skins[Math.floor(Math.random() * skins.length)];
        ctx.fillRect(x + off + 0.5, y, 1, 1.5);
      }
    }
    url = 'url(' + c.toDataURL() + ')';
  } catch (e) {
    url = '';
  }
  crowdCache[base] = url;
  return url;
}

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// Icons of the half-time summary cards (the yellow card is drawn in CSS).
const SUMMARY_ICONS = {
  shots: '<svg viewBox="0 0 40 40"><path d="M15,34 V8 H37 V34" fill="none" stroke="#fff" stroke-width="2.5" stroke-linejoin="round"/>' +
      '<g stroke="rgba(255,255,255,0.35)" stroke-width="0.8"><line x1="22.3" y1="8" x2="22.3" y2="34"/><line x1="29.6" y1="8" x2="29.6" y2="34"/><line x1="15" y1="16.7" x2="37" y2="16.7"/><line x1="15" y1="25.3" x2="37" y2="25.3"/></g>' +
      '<circle cx="7.5" cy="28" r="5.5" fill="#fff" stroke="#111" stroke-width="1"/><polygon points="7.5,25.2 10.1,27.1 9.1,30.2 5.9,30.2 4.9,27.1" fill="#111"/></svg>',
  'on-target': '<svg viewBox="0 0 40 40"><g fill="none" stroke="#22c55e" stroke-width="2.5"><circle cx="20" cy="20" r="16"/><circle cx="20" cy="20" r="10"/></g><circle cx="20" cy="20" r="4" fill="#22c55e"/></svg>',
  corners: '<svg viewBox="0 0 40 40"><path d="M3,10 V37 H30" fill="none" stroke="#e5e7eb" stroke-width="2" stroke-linecap="round"/>' +
      '<path d="M3,27 A10,10 0 0 1 13,37" fill="none" stroke="#e5e7eb" stroke-width="2"/>' +
      '<line x1="8" y1="33" x2="8" y2="4" stroke="#fff" stroke-width="2.5" stroke-linecap="round"/><path d="M9.2,5 C16,2.5 21,8 30,5.5 L25.5,12 L30,18.5 C21,21 16,15.5 9.2,18 Z" fill="#ef4444"/></svg>',
  possession: '<svg viewBox="0 0 40 40"><circle cx="20" cy="20" r="14" fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="6"/>' +
      '<path d="M20,6 A14,14 0 1 1 7.9,27" fill="none" stroke="#4ade80" stroke-width="6"/><circle cx="20" cy="20" r="4.5" fill="#fff"/></svg>',
  passes: '<svg viewBox="0 0 40 40"><circle cx="8" cy="31" r="5" fill="#e5e7eb"/><circle cx="32" cy="9" r="5" fill="#e5e7eb"/>' +
      '<path d="M12,27 Q15,13 25.5,11" fill="none" stroke="#4ade80" stroke-width="2.5" stroke-dasharray="3 2.5" stroke-linecap="round"/>' +
      '<path d="M21.5,7.5 L26.5,11 L22,15" fill="none" stroke="#4ade80" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  saves: '<svg viewBox="0 0 40 40"><g fill="#e5e7eb"><rect x="9" y="16" width="22" height="17" rx="6"/><rect x="9" y="7" width="4.6" height="15" rx="2.3"/>' +
      '<rect x="14.6" y="4" width="4.6" height="17" rx="2.3"/><rect x="20.2" y="5" width="4.6" height="16" rx="2.3"/><rect x="25.8" y="8" width="4.4" height="13" rx="2.2"/></g>' +
      '<rect x="10" y="31" width="20" height="6" rx="1.5" fill="#22c55e"/></svg>',
  tackles: '<svg viewBox="0 0 40 40"><path d="M7,8 H18 V21 L31,25 A5,5 0 0 1 35,30 V32 H7 Z" fill="#e5e7eb"/>' +
      '<g fill="#22c55e"><rect x="9" y="32" width="3.5" height="4" rx="1"/><rect x="17" y="32" width="3.5" height="4" rx="1"/><rect x="25" y="32" width="3.5" height="4" rx="1"/></g>' +
      '<path d="M18,14 H24 M18,18 H27" stroke="#1f2937" stroke-width="1.6" stroke-linecap="round"/></svg>'
};
// How long each card of the half-time rotation stays up (ms).
const SUMMARY_HALFTIME_MS = 8000;
const SUMMARY_SLIDE_MS = 7000;

export default {
  name: 'SoccerStadium',
  mixins: [sirplayLicenceMixin],
  props: {
    // Ball position in pitch percent (0-100 on both axes), as MatchTracker computes it.
    ballX: { type: Number, default: 50 },
    ballY: { type: Number, default: 50 },
    ballVisible: { type: Boolean, default: false },
    // Incident type currently shown (1 corner, 17 throw-in, ...) and its side ('1' home / '2' away).
    actionType: { type: Number, default: 0 },
    actionSide: { type: String, default: null },
    isGoal: { type: Boolean, default: false },
    momentumSide: { type: String, default: null },
    momentumVisible: { type: Boolean, default: false },
    badgeText: { type: String, default: '' },
    // Team shirt colours ('#rrggbb'), used for the stands' crowd and seats.
    homeColor: { type: String, default: '#00e0ff' },
    awayColor: { type: String, default: '#ff3d5a' },
    // { key, kind: yellow|red|penalty|missed-penalty|substitution|goal|on-target|off-target|offside|var|injury|whistle, title, team, side, persistent }
    bigBadge: { type: Object, default: null },
    // { key, title, label, subtitle, team, side, scoreSide, homeName, awayName, fromHome, fromAway, homeScore, awayScore }
    goalCelebration: { type: Object, default: null },
    // Half-time summary cards, see buildBreakSummary (team / player stats).
    breakSlides: { type: Array, default: () => [] },
    time: { type: String, default: '00:00' },
    progress: { type: Number, default: 0 },
    logoUrl: { type: String, default: '' },
    boardText: { type: String, default: 'LIVE' }
  },
  data() {
    return {
      PITCH_LOGO,
      SUMMARY_ICONS,
      STAGE_H,
      scale: 1,
      jitter: { x: 0, y: 0 },
      jitterTimer: null,
      bigBadgeShown: null,
      bigBadgeTimer: null,
      // 0: the "half time" card, n: breakSlides[n - 1].
      summaryIndex: 0,
      summaryTimer: null,
      // The rotation has moved on at least once: cards slide instead of popping.
      summaryCycling: false,
      goalShown: null,
      goalTimers: [],
      goalRolled: false,
      goalAct: 'word',
      rollHome: 0,
      rollAway: 0,
      resizeObserver: null,
      logoReady: false,
      logoAspect: null
    };
  },
  mounted() {
    this.updateScale();
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.updateScale());
      this.resizeObserver.observe(this.$refs.root);
    } else {
      window.addEventListener('resize', this.updateScale);
    }
    // Open play: the ball wanders around its zone so the pitch feels alive.
    this.jitterTimer = setInterval(() => {
      if (this.isOpenPlay) {
        this.jitter = { x: (Math.random() - 0.5) * 10, y: (Math.random() - 0.5) * 36 };
      } else if (this.jitter.x || this.jitter.y) {
        this.jitter = { x: 0, y: 0 };
      }
    }, 1700);
  },
  watch: {
    // Board logo: preload + decode before showing it. Until then every logo
    // slot already has its final-ish width (so the scrolling strip is full
    // from the first frame, instead of collapsing to nothing while the
    // images load — very visible on mobile) and the logos fade in together.
    logoUrl: {
      handler(url) {
        if (!url) { this.logoReady = false; return; }
        if (logoAspectCache[url]) {
          this.logoAspect = logoAspectCache[url];
          this.logoReady = true;
          return;
        }
        this.logoReady = false;
        const img = new Image();
        const done = () => {
          if (this.logoUrl !== url) return;
          if (img.naturalWidth && img.naturalHeight) {
            logoAspectCache[url] = img.naturalWidth / img.naturalHeight;
            this.logoAspect = logoAspectCache[url];
          }
          this.logoReady = true;
        };
        img.onload = () => {
          if (img.decode) img.decode().then(done, done);
          else done();
        };
        img.onerror = done;
        img.src = url;
      },
      immediate: true
    },
    'goalCelebration.key': {
      handler() {
        this.goalTimers.forEach(clearTimeout);
        this.goalTimers = [];
        const g = this.goalCelebration;
        this.goalShown = g;
        this.goalRolled = false;
        if (!g) return;
        this.rollHome = g.fromHome;
        this.rollAway = g.fromAway;
        this.goalAct = 'word';
        // Act 2 (split screen) after the "GOL!" hold, then the number rolls,
        // then the panel leaves.
        this.goalTimers.push(setTimeout(() => { this.goalAct = 'split'; }, 2600));
        this.goalTimers.push(setTimeout(() => {
          this.goalRolled = true;
          this.rollHome = this.goalCelebration ? this.goalCelebration.homeScore : this.rollHome;
          this.rollAway = this.goalCelebration ? this.goalCelebration.awayScore : this.rollAway;
        }, 4300));
        this.goalTimers.push(setTimeout(() => { this.goalShown = null; }, 8200));
      },
      immediate: true
    },
    // Scoreboard catching up after the goal was announced: roll then.
    'goalCelebration.homeScore'(v) { if (this.goalShown && this.goalRolled) this.rollHome = v; },
    'goalCelebration.awayScore'(v) { if (this.goalShown && this.goalRolled) this.rollAway = v; },
    // The big badge pops in for a few seconds, then gets out of the way of
    // the pitch (the incident strip below keeps showing the incident).
    'bigBadge.key': {
      handler() {
        clearTimeout(this.bigBadgeTimer);
        this.bigBadgeShown = this.bigBadge;
        if (this.bigBadge && !this.bigBadge.persistent) {
          this.bigBadgeTimer = setTimeout(() => { this.bigBadgeShown = null; }, this.bigBadge.kind === 'goal' ? 8000 : 6000);
        }
      },
      immediate: true
    },
    // Half time with something to sum up: start the rotation, which goes
    // back to the plain badge as soon as the break is over.
    summaryRotating: {
      handler(on) {
        clearTimeout(this.summaryTimer);
        this.summaryIndex = 0;
        this.summaryCycling = false;
        if (on) this.scheduleSummary();
      },
      immediate: true
    },
    // A persistent badge (VAR review) whose phase is over: let it go.
    'bigBadge.persistent'(v, old) {
      if (old && !v && this.bigBadgeShown && this.bigBadge && this.bigBadgeShown.key === this.bigBadge.key) {
        clearTimeout(this.bigBadgeTimer);
        this.bigBadgeTimer = setTimeout(() => { this.bigBadgeShown = null; }, 1500);
      }
    }
  },
  beforeDestroy() {
    this.goalTimers.forEach(clearTimeout);
    clearTimeout(this.bigBadgeTimer);
    clearTimeout(this.summaryTimer);
    clearInterval(this.jitterTimer);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    else window.removeEventListener('resize', this.updateScale);
  },
  methods: {
    // Next card of the half-time rotation, back to the "half time" one after the last.
    scheduleSummary() {
      this.summaryTimer = setTimeout(() => {
        this.summaryCycling = true;
        this.summaryIndex = (this.summaryIndex + 1) % (this.breakSlides.length + 1);
        this.scheduleSummary();
      }, this.summaryIndex === 0 ? SUMMARY_HALFTIME_MS : SUMMARY_SLIDE_MS);
    },
    formatSummary(value) {
      return Math.round(value * 10) / 10;
    },
    updateScale() {
      const el = this.$refs.root;
      if (!el) return;
      const w = el.clientWidth || STAGE_W;
      this.scale = w / STAGE_W;
    }
  },
  computed: {
    // The half-time card is up and there are stats to show with it.
    summaryRotating() {
      const b = this.bigBadgeShown;
      return !!b && b.persistent && b.kind === 'whistle' && this.breakSlides.length > 0;
    },
    // Summary card on screen, null while the "half time" card is.
    summarySlide() {
      if (!this.summaryRotating || !this.summaryIndex) return null;
      return this.breakSlides[(this.summaryIndex - 1) % this.breakSlides.length];
    },
    // The default Sirplay logo (no skin logo) is drawn bigger.
    isDefaultBoardLogo() {
      return this.logoUrl === BOARD_LOGO;
    },
    // Fixed slot for every board logo: the logo's own ratio at the board's
    // logo height once known, a sensible width before that.
    logoSlotWidth() {
      const h = this.isDefaultBoardLogo ? 18 : 17;
      if (!this.logoAspect) return BOARD_LOGO_FALLBACK_WIDTH;
      const w = h * this.logoAspect;
      return this.isDefaultBoardLogo ? w : Math.min(w, 120);
    },
    logoSlotStyle() {
      return { width: this.logoSlotWidth + 'px' };
    },
    // Enough logos per half for a half to be at least as long as its board,
    // so the looping strip never shows an empty stretch.
    farLogoCount() {
      return Math.ceil((PW + END_GAP * 2) / (this.logoSlotWidth + BOARD_LOGO_GAP)) + 1;
    },
    endLogoCount() {
      return Math.ceil(this.endBoardLength / (this.logoSlotWidth + BOARD_LOGO_GAP)) + 1;
    },
    crowdHome() {
      return crowdTexture(this.homeColor);
    },
    crowdAway() {
      return crowdTexture(this.awayColor);
    },
    attacksRight() {
      return this.actionSide !== '2';
    },
    isOpenPlay() {
      return this.ballVisible && [0, 4, 5, 11, 2430, 2438].includes(this.actionType);
    },
    // Ball position in pitch percent, with set pieces snapped to where they
    // really happen (the corner arc, the touchline, the penalty spot).
    ball() {
      let x = this.ballX;
      let y = this.ballY;
      const t = this.actionType;
      if (t === 1) {
        x = this.attacksRight ? 100 : 0;
        y = 0;
      } else if (t === 17) {
        x = this.attacksRight ? 74 : 26;
        y = 100;
      } else if (t === 8 || t === 40) {
        x = this.attacksRight ? (94 / 105) * 100 : (11 / 105) * 100;
        y = 50;
      } else if (this.isGoal) {
        x = this.attacksRight ? 101.5 : -1.5;
        y = 50;
      }
      if (this.isOpenPlay) {
        x = clamp(x + this.jitter.x, 3, 97);
        y = clamp(y + this.jitter.y, 6, 94);
      }
      return { x, y };
    },
    ballWorld() {
      return { x: (this.ball.x / 100) * PW, y: (this.ball.y / 100) * PH };
    },
    // Camera: wide shot in midfield; pans and zooms toward an end when the
    // ball gets into the final third (or for a set piece down there).
    camera() {
      const bx = this.ballVisible ? this.ballX : 50;
      const t = this.actionType;
      let end = 0;
      if (this.ballVisible) {
        if (t === 1 || t === 8 || t === 40 || t === 17 || this.isGoal) end = this.attacksRight ? 1 : -1;
        else if (bx >= 66) end = 1;
        else if (bx <= 34) end = -1;
      }
      if (end !== 0) {
        return { fx: end * PW * 0.25, fy: 30, tz: -10 };
      }
      return { fx: ((bx - 50) / 50) * PW * 0.06, fy: -6, tz: -112 };
    },
    worldStyle() {
      const c = this.camera;
      return {
        transform: `translate3d(0px, 22px, ${c.tz}px) rotateX(${TILT}deg) translate(${-c.fx}px, ${-c.fy}px)`
      };
    },
    groundStyle() {
      return { left: -END_GAP - 6 + 'px', right: -END_GAP - 6 + 'px', top: -FAR_GAP - 4 + 'px', bottom: '-26px' };
    },
    farBoardStyle() {
      const w = PW + END_GAP * 2;
      return {
        left: -END_GAP + 'px', width: w + 'px', height: BOARD_H + 'px',
        top: -FAR_GAP - BOARD_H + 'px'
      };
    },
    endBoardLength() {
      return PH + FAR_GAP + 70;
    },
    endBoards() {
      const common = { width: BOARD_H + 'px', top: -FAR_GAP + 'px', height: this.endBoardLength + 'px' };
      return [
        { side: 'left', style: { ...common, left: -END_GAP - BOARD_H + 'px' } },
        { side: 'right', style: { ...common, left: PW + END_GAP + 'px' } }
      ];
    },
    endStripStyle() {
      return { width: this.endBoardLength + 'px', height: BOARD_H + 'px' };
    },
    farStandStyle() {
      const h = 330;
      return {
        left: -520 + 'px', width: PW + 1040 + 'px', height: h + 'px',
        top: -FAR_GAP - 3 - h + 'px'
      };
    },
    leftStandStyle() {
      const w = 330;
      return {
        left: -END_GAP - 3 - w + 'px', width: w + 'px',
        top: -FAR_GAP - 3 + 'px', height: PH + 200 + 'px'
      };
    },
    rightStandStyle() {
      const w = 330;
      return {
        left: PW + END_GAP + 3 + 'px', width: w + 'px',
        top: -FAR_GAP - 3 + 'px', height: PH + 200 + 'px'
      };
    },
    sidelineLogosStyle() {
      return { left: '0px', top: PH + SIDELINE_GAP + 'px', width: PW + 'px', height: SIDELINE_H + 'px' };
    },
    goals() {
      const scoredSide = this.isGoal ? (this.attacksRight ? 'right' : 'left') : null;
      return [
        { side: 'left', scored: scoredSide === 'left', style: { left: '0px', top: PH / 2 + 'px', transform: 'scale3d(1.35, 1.35, 1.35)' } },
        { side: 'right', scored: scoredSide === 'right', style: { left: PW + 'px', top: PH / 2 + 'px', transform: 'scale3d(-1.35, 1.35, 1.35)' } }
      ];
    },
    flags() {
      return [[0, 0], [PW, 0], [0, PH], [PW, PH]].map(([x, y]) => ({
        key: 'flag-' + x + '-' + y,
        style: { left: x + 'px', top: y + 'px' }
      }));
    },
    ballStyle() {
      return {
        left: this.ballWorld.x + 'px', top: this.ballWorld.y + 'px',
        opacity: this.ballVisible ? 1 : 0
      };
    },
    badgeStyle() {
      // Keep the badge inside the pitch so it never floats over the stands.
      return {
        left: clamp(this.ballWorld.x, 45, PW - 45) + 'px',
        top: clamp(this.ballWorld.y, 20, PH) + 'px'
      };
    },
    ballShadowStyle() {
      return {
        left: this.ballWorld.x + 'px', top: this.ballWorld.y + 'px',
        opacity: this.ballVisible ? 1 : 0
      };
    },
    cone() {
      if (!this.ballVisible) return null;
      const t = this.actionType;
      const b = this.ballWorld;
      const dir = this.attacksRight ? 1 : -1;
      const goalX = this.attacksRight ? PW : 0;
      const spotX = this.attacksRight ? PW - (11 / 105) * PW : (11 / 105) * PW;
      let angle, len, half;
      if (t === 1) {
        // Corner: from the flag toward the penalty spot, covering the box.
        angle = Math.atan2(PH / 2 - b.y, spotX - b.x);
        len = Math.hypot(PH / 2 - b.y, spotX - b.x) * 1.05;
        half = 24;
      } else if (t === 17) {
        // Throw-in: a wide fan from the near touchline into the pitch.
        angle = -Math.PI / 2 + dir * (8 * Math.PI / 180);
        len = 78;
        half = 68;
      } else if (t === 13 || t === 2394 || t === 8) {
        // Free kick / penalty: toward the goal.
        angle = Math.atan2(PH / 2 - b.y, goalX - b.x);
        len = clamp(Math.hypot(PH / 2 - b.y, goalX - b.x), 40, 170);
        half = t === 8 ? 16 : 14;
      } else if (t === 14) {
        // Goal kick: from the goal area out toward midfield.
        angle = Math.atan2(PH / 2 - b.y, PW / 2 - b.x);
        len = 150;
        half = 20;
      } else {
        return null;
      }
      // Circular sector centred on the ball: a round element clipped to the
      // wedge between angle-half and angle+half (fan points just outside the
      // circle, so the rounded edge comes from the border-radius).
      const h = half * Math.PI / 180;
      const steps = Math.max(2, Math.ceil((2 * h) / (Math.PI / 6)));
      const pts = ['50% 50%'];
      for (let i = 0; i <= steps; i++) {
        const a = angle - h + (2 * h * i) / steps;
        pts.push(`${(50 + 72 * Math.cos(a)).toFixed(2)}% ${(50 + 72 * Math.sin(a)).toFixed(2)}%`);
      }
      return {
        key: `${t}-${Math.round(b.x)}-${Math.round(b.y)}`,
        style: {
          left: b.x - len + 'px', top: b.y - len + 'px', width: len * 2 + 'px', height: len * 2 + 'px',
          transform: 'translateZ(0.4px)'
        },
        fillStyle: { clipPath: `polygon(${pts.join(', ')})` }
      };
    }
  }
};
</script>

<style scoped>
.ss-root { position: relative; width: 100%; overflow: hidden; background: #0b0b0d; }
.ss-stage { position: absolute; top: 0; left: 0; width: 480px; height: 240px; transform-origin: 0 0; }
.ss-viewport {
  position: absolute; inset: 0; overflow: hidden;
  perspective: 430px; perspective-origin: 50% 30%;
  background: linear-gradient(#0a0a0c 0%, #17171a 40%, #0c0c0e 100%);
}

/* World = the pitch rectangle lying on the ground; its transform is the camera. */
.ss-world {
  position: absolute; left: 30px; top: -16px; width: 420px; height: 272px;
  transform-origin: 50% 50%; transform-style: preserve-3d;
  transition: transform 1.4s cubic-bezier(.45, .05, .25, 1);
}
.ss-world > * { position: absolute; }

/* ---------- stands ---------- */
.ss-stand { transform-style: flat; background: #141416; }
.ss-stand--far { transform-origin: 50% 100%; transform: translateZ(19px) rotateX(-57deg); }
.ss-stand--left { transform-origin: 100% 50%; transform: translateZ(19px) rotateY(57deg); }
.ss-stand--right { transform-origin: 0% 50%; transform: translateZ(19px) rotateY(-57deg); }
.ss-crowd { position: absolute; top: 0; bottom: 0; background-size: 90px 45px; background-repeat: repeat; filter: brightness(0.7) saturate(1.15) blur(0.35px); }
.ss-crowd--home { left: 0; width: 50%; }
.ss-crowd--away { right: 0; width: 50%; }
.ss-crowd--full { left: 0; right: 0; }
/* Lower tiers sit in the roof's shadow, upper tiers catch the floodlights. */
.ss-stand--far::after { content: ''; position: absolute; inset: 0; background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.05) 70%, rgba(0,0,0,0.45) 100%); }
.ss-stand--left::after { content: ''; position: absolute; inset: 0; background: linear-gradient(to left, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.05) 70%, rgba(0,0,0,0.45) 100%); }
.ss-stand--right::after { content: ''; position: absolute; inset: 0; background: linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.05) 70%, rgba(0,0,0,0.45) 100%); }

/* ---------- grass ---------- */
.ss-ground { background: #2c6a2d; }
.ss-pitch {
  left: 0; top: 0; width: 420px; height: 272px; transform: translateZ(0.1px);
  background:
    radial-gradient(ellipse 70% 80% at 50% 45%, rgba(255,255,255,0.07) 0%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.18) 100%),
    repeating-linear-gradient(90deg, #3d8b3b 0px, #3d8b3b 26.25px, #378236 26.25px, #378236 52.5px);
  box-shadow: 0 0 0 14px rgba(44,106,45,0.001);
}
/* The stand's shadow falling across the right half of the pitch. */
.ss-pitch-shadow { position: absolute; inset: 0; background: linear-gradient(97deg, rgba(0,0,0,0) 49.6%, rgba(0,0,0,0.2) 50.4%, rgba(0,0,0,0.12) 80%, rgba(0,0,0,0.06) 100%); pointer-events: none; }
.ss-lines { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }

/* Momentum: a coloured wedge in the attacking half. */
.ss-momentum { position: absolute; inset: 0; opacity: 0; transition: opacity 0.5s ease, clip-path 0.6s ease; }
.ss-momentum.is-visible { opacity: 0.45; animation: ss-momentum-pulse 2s ease-in-out infinite; }
.ss-momentum.is-home { background: linear-gradient(90deg, transparent 35%, var(--color-home, #00e0ff) 100%); clip-path: polygon(35% 0%, 88% 0%, 100% 50%, 88% 100%, 35% 100%, 45% 50%); }
.ss-momentum.is-away { background: linear-gradient(270deg, transparent 35%, var(--color-away, #ff3d5a) 100%); clip-path: polygon(65% 0%, 12% 0%, 0% 50%, 12% 100%, 65% 100%, 55% 50%); }
/* Plain possession: the team's own half, the wedge pointing toward the
   halfway line (same 6-point shapes, so it morphs into/out of an attack). */
.ss-momentum.is-possession.is-home { background: linear-gradient(90deg, transparent 0%, var(--color-home, #00e0ff) 50%); clip-path: polygon(0% 0%, 42% 0%, 50% 50%, 42% 100%, 0% 100%, 8% 50%); }
.ss-momentum.is-possession.is-away { background: linear-gradient(270deg, transparent 0%, var(--color-away, #ff3d5a) 50%); clip-path: polygon(100% 0%, 58% 0%, 50% 50%, 58% 100%, 100% 100%, 92% 50%); }
@keyframes ss-momentum-pulse { 0%, 100% { opacity: 0.3; } 50% { opacity: 0.55; } }

/* ---------- set-piece cone ---------- */
.ss-cone { pointer-events: none; }
.ss-cone-fill {
  width: 100%; height: 100%; border-radius: 50%; transform-origin: 50% 50%;
  /* Full colour at the vertex (the ball), fading out toward the arc:
     closest-side makes 100% land exactly on the circle's edge. */
  background: radial-gradient(circle closest-side at 50% 50%,
    rgba(225, 255, 232, 0.95) 0%,
    rgba(140, 236, 170, 0.78) 18%,
    rgba(110, 225, 150, 0.45) 55%,
    rgba(110, 225, 150, 0.12) 88%,
    rgba(110, 225, 150, 0) 100%);
  /* Ping: grows out of the vertex, then fades away, and again. */
  animation: ss-cone-ping 1.8s cubic-bezier(.15, .7, .35, 1) infinite;
}
@keyframes ss-cone-ping {
  0% { transform: scale(0.05); opacity: 0; }
  12% { opacity: 1; }
  65% { transform: scale(1); opacity: 0.95; }
  100% { transform: scale(1.06); opacity: 0; }
}

/* ---------- ad-boards ---------- */
.ss-board {
  overflow: hidden;
  background: linear-gradient(#10251a, #081410);
  border-top: 1px solid rgba(255,255,255,0.25);
}
.ss-board--far { transform-origin: 50% 100%; transform: rotateX(-90deg); }
.ss-board--end { border-top: 0; }
.ss-board--left { transform-origin: 100% 50%; transform: rotateY(90deg); }
.ss-board--right { transform-origin: 0% 50%; transform: rotateY(-90deg); }
.ss-board-strip {
  position: absolute; top: 50%; left: 50%; overflow: hidden;
  transform: translate(-50%, -50%) rotate(90deg);
}
.ss-board-track { display: flex; height: 100%; width: max-content; animation: ss-board-scroll 50s linear infinite; }
.ss-board--end .ss-board-track { animation-duration: 36s; }
.ss-board-half { display: flex; align-items: center; gap: 26px; padding-left: 26px; flex-shrink: 0; height: 100%; } /* gap = BOARD_LOGO_GAP */
@keyframes ss-board-scroll { from { transform: translateX(-50%); } to { transform: translateX(0); } }
.ss-board--left .ss-board-strip { transform: translate(-50%, -50%) rotate(-90deg); }
.ss-board-logo { height: 17px; max-width: none; object-fit: contain; flex-shrink: 0; opacity: 0; transition: opacity 0.4s ease; }
.ss-board-logo.is-default { height: 18px; }
.ss-board-logo.is-ready { opacity: 1; }
.ss-board-text { font: 900 var(--font-size-2xl)/1 'Roboto', sans-serif; color: #f5d90a; text-transform: uppercase; white-space: nowrap; }

/* ---------- touchline logo ---------- */
.ss-sideline-logos {
  display: flex; align-items: center; justify-content: center;
  transform: translateZ(0.2px); pointer-events: none;
}
.ss-sideline-logo { height: 100%; width: auto; opacity: 0.95; }

/* ---------- goals ---------- */
.ss-goal { width: 0; height: 0; transform-style: preserve-3d; transform-origin: 0 0; }
.ss-goal > div { position: absolute; }
.ss-goal-frame {
  left: -15px; top: -19px; width: 15px; height: 38px;
  transform-origin: 100% 50%; transform: rotateY(90deg);
  border: 1.6px solid #fff; border-right: 0; box-sizing: border-box;
  box-shadow: 0 0 2px rgba(0,0,0,0.4);
}
.ss-net {
  background:
    repeating-linear-gradient(0deg, rgba(255,255,255,0.45) 0 0.6px, transparent 0.6px 3px),
    repeating-linear-gradient(90deg, rgba(255,255,255,0.45) 0 0.6px, transparent 0.6px 3px),
    rgba(255,255,255,0.1);
}
.ss-net--back { left: -28px; top: -19px; width: 15px; height: 38px; transform-origin: 100% 50%; transform: rotateY(90deg); }
.ss-net--top { left: -13px; top: -19px; width: 13px; height: 38px; transform: translateZ(15px); }
.ss-net--side { left: -13px; width: 13px; height: 15px; transform-origin: 50% 100%; transform: rotateX(-90deg); }
.ss-net--side-a { top: -34px; }
.ss-net--side-b { top: 4px; }
.ss-goal.is-scored .ss-net { animation: ss-net-shake 0.35s ease-in-out 4; }
@keyframes ss-net-shake { 0%, 100% { filter: brightness(1); } 50% { filter: brightness(1.8); } }

/* ---------- corner flags ---------- */
.ss-flag {
  width: 7px; height: 16px; margin-top: -16px;
  transform-origin: 0 100%; transform: rotateX(-90deg);
  border-left: 1px solid #f1f1f1;
}
.ss-flag::after { content: ''; position: absolute; left: 0; top: 0; width: 6px; height: 5px; background: #e0283f; clip-path: polygon(0 0, 100% 50%, 0 100%); }

/* ---------- ball ---------- */
.ss-ball, .ss-ball-shadow { transition: left 0.9s cubic-bezier(.3, .8, .3, 1), top 0.9s cubic-bezier(.3, .8, .3, 1), opacity 0.4s ease; }
.ss-ball {
  width: 9px; height: 9px; margin: -9px 0 0 -4.5px;
  transform-origin: 50% 100%; transform: translateZ(0.6px) rotateX(-58deg);
}
.ss-ball-sprite {
  display: block; width: 100%; height: 100%; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #fff 0%, #f1f1f1 45%, #9a9a9a 100%);
  box-shadow: 0 0 5px rgba(255,255,255,0.6);
}
.ss-ball-shadow {
  width: 11px; height: 7px; margin: -3px 0 0 -4px; border-radius: 50%;
  background: radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 70%);
  transform: translateZ(0.3px);
}

/* ---------- incident badge ---------- */
.ss-badge-anchor {
  width: 0; height: 0;
  transform-origin: 0 0; transform: translateZ(16px) rotateX(-58deg);
  transition: left 0.9s cubic-bezier(.3, .8, .3, 1), top 0.9s cubic-bezier(.3, .8, .3, 1);
}
.ss-badge {
  position: absolute; left: 0; bottom: 0; transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.62); color: #fff; border-radius: 4px;
  padding: 4px 9px; font: 700 var(--font-size-xl)/1.2 'Roboto', sans-serif; white-space: nowrap;
  animation: ss-badge-in 0.35s cubic-bezier(.2, .8, .3, 1.2) both;
}
@keyframes ss-badge-in { from { opacity: 0; transform: translateX(-50%) translateY(6px) scale(0.8); } to { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); } }

/* ---------- big incident badge ---------- */
/* Broadcast card: dark green glass, the incident name in heavy italic
   capitals stacked on the left with its icon beside it, the team centred
   underneath in light green. The text/icon markup is flattened into one grid
   (display: contents) so the team line can span the whole card. */
.ss-big-badge {
  position: absolute; left: 50%; top: 44%; z-index: 3;
  transform: translate(-50%, -50%);
  display: grid; grid-template-columns: auto auto; grid-template-areas: "title icon" "team team";
  align-items: center; justify-content: center; column-gap: 12px; row-gap: 7px;
  max-width: 86%; box-sizing: border-box;
  padding: 13px 18px 11px; border-radius: 7px;
  background: linear-gradient(160deg, rgba(40, 70, 50, 0.93) 0%, rgba(22, 44, 31, 0.9) 100%);
  -webkit-backdrop-filter: blur(3px); backdrop-filter: blur(3px);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.45), inset 0 0 0 1px rgba(255, 255, 255, 0.07);
}
.ss-big-badge-icon { grid-area: icon; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; }
.ss-big-badge-icon svg { width: 100%; height: 100%; display: block; }
.ss-icon-card { width: 24px; height: 34px; border-radius: 3px; transform: rotate(10deg); box-shadow: 0 3px 8px rgba(0, 0, 0, 0.5), inset 0 0 0 1px rgba(255, 255, 255, 0.25); }
.ss-big-badge.is-yellow .ss-icon-card { background: linear-gradient(135deg, #fde047, #eab308); }
.ss-big-badge.is-red .ss-icon-card { background: linear-gradient(135deg, #f87171, #dc2626); }
.ss-big-badge-text { display: contents; }
.ss-big-badge-title {
  /* min-content: one word per line, as broadcast cards stack them. */
  grid-area: title; width: min-content;
  font: italic 900 var(--font-size-7xl)/1.02 'Roboto', sans-serif; color: #fff; text-transform: uppercase;
  text-align: right;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}
.ss-big-badge-team {
  grid-area: team; text-align: center;
  font: 600 var(--font-size-3xl)/1.2 'Roboto', sans-serif; color: #86efac;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ss-big-badge.is-goal .ss-big-badge-icon { width: 46px; height: 46px; animation: ss-goal-spin 1.2s ease-out both; }
.ss-big-badge.is-goal .ss-big-badge-title { font-size: var(--font-size-10xl); color: #4ade80; text-shadow: 0 0 12px rgba(74, 222, 128, 0.6); }
@keyframes ss-goal-spin { from { transform: rotate(-360deg) scale(0.4); } to { transform: rotate(0) scale(1); } }
.ss-big-badge-enter-active { transition: opacity 0.3s ease, transform 0.4s cubic-bezier(.2, .9, .3, 1.25); }
.ss-big-badge-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.ss-big-badge-enter, .ss-big-badge-leave-to { opacity: 0; transform: translate(-50%, -50%) scale(0.75); }

/* Half-time summary: the same card at a fixed width (so it does not jump
   from one stat to the next), the title and icon on top, then the values. */
.ss-big-badge.is-summary {
  width: 300px; grid-template-areas: "title icon" "row row" "bar bar";
}
.ss-summary-icon { display: block; width: 100%; height: 100%; }
.ss-summary-icon ::v-deep svg { width: 100%; height: 100%; display: block; }
.ss-summary-row {
  grid-area: row; min-width: 0;
  display: grid; grid-template-columns: minmax(0, 1fr) auto auto auto minmax(0, 1fr); align-items: center; column-gap: 8px;
}
.ss-summary-name {
  font: 600 var(--font-size-xl)/1.2 'Roboto', sans-serif; color: #86efac;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ss-summary-name.is-home { text-align: right; }
.ss-summary-value {
  font: 800 var(--font-size-5xl)/1 'Roboto', sans-serif; color: rgba(255, 255, 255, 0.55);
  font-variant-numeric: tabular-nums;
}
.ss-summary-value.is-lead { color: #fff; text-shadow: 0 0 10px rgba(74, 222, 128, 0.55); }
.ss-summary-sep { font: 700 var(--font-size-3xl)/1 'Roboto', sans-serif; color: rgba(255, 255, 255, 0.4); }
.ss-summary-bar {
  grid-area: bar; display: flex; gap: 2px; height: 4px; border-radius: 2px; overflow: hidden;
  background: rgba(255, 255, 255, 0.12);
}
.ss-summary-bar-home { background: var(--color-home, #00e0ff); flex-basis: 0; transition: flex-grow 0.4s ease; }
.ss-summary-bar-away { background: var(--color-away, #ff3d5a); flex-basis: 0; transition: flex-grow 0.4s ease; }
.ss-summary-row--player { grid-template-columns: auto minmax(0, 1fr) auto; column-gap: 10px; }
.ss-summary-dot { width: 10px; height: 10px; border-radius: 50%; box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2); }
.ss-summary-dot.is-home { background: var(--color-home, #00e0ff); }
.ss-summary-dot.is-away { background: var(--color-away, #ff3d5a); }
.ss-summary-dot.is-neutral { background: #94a3b8; }
.ss-summary-player { display: flex; flex-direction: column; min-width: 0; }
.ss-summary-player-name {
  font: 700 var(--font-size-3xl)/1.15 'Roboto', sans-serif; color: #fff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ss-summary-player-team {
  font: 600 var(--font-size-lg)/1.2 'Roboto', sans-serif; color: #86efac;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
/* One card after the other: out to the left, in from the right. */
.ss-badge-swap-enter-active { transition: opacity 0.35s ease, transform 0.45s cubic-bezier(.2, .9, .3, 1.1); }
.ss-badge-swap-leave-active { transition: opacity 0.25s ease, transform 0.3s ease-in; }
.ss-badge-swap-enter { opacity: 0; transform: translate(calc(-50% + 48px), -50%); }
.ss-badge-swap-leave-to { opacity: 0; transform: translate(calc(-50% - 48px), -50%); }

/* ---------- goal celebration ---------- */
.ss-goal-panel {
  /* Team colours set by MatchTracker from the teams' shirt colours
     (default celeste / red). */
  --goal-home: var(--color-home, #00e0ff);
  --goal-away: var(--color-away, #ff3d5a);
  --goal-color: var(--goal-home);
  position: absolute; inset: 0; z-index: 6; overflow: hidden;
  background: linear-gradient(100deg, rgba(6, 16, 10, 0.96) 0%, rgba(12, 34, 20, 0.95) 50%, rgba(6, 16, 10, 0.96) 100%);
  border-bottom: 2px solid var(--goal-color);
  box-sizing: border-box;
}
.ss-goal-panel.is-away { --goal-color: var(--goal-away); }
.ss-goal-act { position: absolute; inset: 0; }

/* Act 1: "GOL!" + team */
.ss-goal-act--word { display: flex; flex-direction: column; align-items: center; justify-content: center; }
.ss-goal-word {
  font: italic 900 var(--font-size-20xl)/0.95 'Roboto', sans-serif; color: #fff; text-transform: uppercase;
  text-shadow: 0 0 18px var(--goal-color), 0 3px 0 rgba(0, 0, 0, 0.45);
  animation: ss-goal-word-in 0.55s cubic-bezier(.2, 1.4, .4, 1) 0.3s both;
}
@keyframes ss-goal-word-in { from { opacity: 0; transform: scale(2.2); } to { opacity: 1; transform: scale(1); } }
.ss-goal-team-line {
  margin-top: 6px; font: 700 var(--font-size-4xl)/1.2 'Roboto', sans-serif; color: #86efac;
  animation: ss-goal-fade-up 0.4s ease-out 0.6s both;
}
@keyframes ss-goal-fade-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.ss-goal-act-enter-active { transition: opacity 0.35s ease; }
.ss-goal-act-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.ss-goal-act-enter { opacity: 0; }
.ss-goal-act-leave-to { opacity: 0; transform: translateY(-18px); }

/* Act 2: split screen in the teams' colours */
.ss-goal-act--split { display: flex; }
.ss-goal-half {
  position: relative; flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
  animation: ss-goal-half-in 0.45s cubic-bezier(.2, .9, .2, 1) both;
}
/* Team colour, darkening toward the outer edge. */
.ss-goal-half--home { --half-text: var(--color-home-contrast, #fff); }
.ss-goal-half--away { --half-text: var(--color-away-contrast, #fff); }
.ss-goal-half--home { background: linear-gradient(90deg, rgba(255, 255, 255, 0.14) 0%, rgba(0, 0, 0, 0.32) 100%), var(--goal-home); }
.ss-goal-half--away { background: linear-gradient(90deg, rgba(255, 255, 255, 0.08) 0%, rgba(0, 0, 0, 0.4) 100%), var(--goal-away); animation-delay: 0.08s; }
@keyframes ss-goal-half-in { from { transform: translateY(100%); } to { transform: translateY(0); } }
/* Names roll up from the bottom like the score digits. */
.ss-goal-roll-in { display: block; overflow: hidden; }
.ss-goal-roll-in > span { display: block; animation: ss-goal-roll-up 0.55s cubic-bezier(.3, 1.3, .5, 1) both; }
.ss-goal-half-name { max-width: 90%; margin-bottom: 4px; }
.ss-goal-half-name > span {
  font: 800 var(--font-size-3xl)/1.3 'Roboto', sans-serif; color: var(--half-text, #fff); text-transform: uppercase;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  animation-delay: 0.45s;
}
.ss-goal-half--away .ss-goal-half-name > span { animation-delay: 0.55s; }
@keyframes ss-goal-roll-up { from { transform: translateY(110%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.ss-goal-big-digit { position: relative; width: 110px; height: 104px; overflow: hidden; }
.ss-goal-big-digit-value {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font: italic 900 calc(var(--font-size-20xl) * 1.5)/1 'Roboto', sans-serif; color: var(--half-text, #fff); font-variant-numeric: tabular-nums;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
/* First appearance of the digits: same roll-up as the names (not for the
   number rolling to the new score later, which uses .ss-roll). */
.ss-goal-big-digit-value.is-intro { animation: ss-goal-roll-up 0.55s cubic-bezier(.3, 1.3, .5, 1) 0.65s both; }
/* Score roll, one number at a time (mode="out-in"): the old one leaves
   through the top first, only then the new one comes up from the bottom —
   never both on screen. No overshoot, so the new one can't creep into the
   old one's space either. */
.ss-roll-leave-active { transition: transform 0.22s cubic-bezier(.5, 0, .9, .4); }
.ss-roll-leave-to { transform: translateY(-110%); }
.ss-roll-enter-active { transition: transform 0.38s cubic-bezier(.15, .85, .35, 1); }
.ss-roll-enter { transform: translateY(110%); }
.ss-goal-caption {
  position: absolute; left: 50%; bottom: 18px; transform: translateX(-50%);
  max-width: 90%; box-sizing: border-box; padding: 4px 12px; border-radius: 4px;
  background: rgba(0, 0, 0, 0.45); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  font: 800 var(--font-size-2xl)/1.2 'Roboto', sans-serif; color: #fff; text-transform: uppercase;
  animation: ss-goal-caption-in 0.4s ease-out 0.9s both;
}
@keyframes ss-goal-caption-in { from { opacity: 0; transform: translate(-50%, 10px); } to { opacity: 1; transform: translate(-50%, 0); } }
.ss-goal-panel-enter-active { transition: transform 0.5s cubic-bezier(.2, .9, .2, 1); }
.ss-goal-panel-leave-active { transition: transform 0.45s cubic-bezier(.6, 0, .8, .3); }
.ss-goal-panel-enter { transform: translateX(-105%); }
.ss-goal-panel-leave-to { transform: translateX(105%); }

/* ---------- overlays ---------- */
.ss-roof { position: absolute; top: 0; left: 0; width: 100%; height: 26px; pointer-events: none; }
.ss-clock {
  position: absolute; top: 3px; left: 50%; transform: translateX(-50%);
  display: flex; flex-direction: column; align-items: stretch; z-index: 2;
}
.ss-clock-progress { display: block; height: 2px; background: rgba(255,255,255,0.12); border-radius: 1px; overflow: hidden; margin: 0 2px 2px; }
.ss-clock-progress span { display: block; height: 100%; background: #e2345a; transition: width 0.6s linear; }
.ss-clock-time {
  background: rgb(68 143 66 / 15%); border: 1px solid rgba(255,255,255,0.06); border-radius: 6px;
  padding: 2px 9px; font: 700 var(--font-size-3xl)/1.2 'Roboto Mono', 'Roboto', monospace;
  color: #34e0b9;
}
</style>
